import type { EmberColorPalette } from '@athrelaris/shared';
import { useEffect } from 'react';

// Maps each EmberColorPalette field to the CSS custom property that
// Tailwind's tokens read via `rgb(var(--…) / <alpha-value>)`. Hex tokens
// drive composed properties (hairlineRgba) directly.
const RGB_VARS: Record<string, keyof EmberColorPalette> = {
  '--color-bg-deep': 'bgDeepRgb',
  '--color-bg': 'bgRgb',
  '--color-surface': 'surfaceRgb',
  '--color-surface-elevated': 'surfaceElevatedRgb',
  '--color-text-bright': 'textBrightRgb',
  '--color-text': 'textPrimaryRgb',
  '--color-text-soft': 'textSoftRgb',
  '--color-text-secondary': 'textSecondaryRgb',
  '--color-text-tertiary': 'textTertiaryRgb',
  '--color-text-deep': 'textDeepRgb',
  '--color-text-deepest': 'textDeepestRgb',
  '--color-accent': 'accentRgb',
  '--color-accent-strong': 'accentStrongRgb',
};

const CACHE_KEY = 'athrelaris.bondPalette.v1';

/**
 * Cache shape: localStorage stores either nothing (no cached palette)
 * or a JSON-serialized EmberColorPalette. v1 prefix lets us bump the
 * key when the palette schema changes.
 */
function readCachedPalette(): EmberColorPalette | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as EmberColorPalette;
  } catch {
    return null;
  }
}

function writeCachedPalette(palette: EmberColorPalette | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (palette) {
      window.localStorage.setItem(CACHE_KEY, JSON.stringify(palette));
    } else {
      window.localStorage.removeItem(CACHE_KEY);
    }
  } catch {
    // localStorage may throw in private mode / when full; theme still
    // works, just no caching across reloads.
  }
}

/**
 * Apply a palette directly to :root. Used both inside the React hook
 * and at module load time so the bond's palette is on screen before
 * the first render flush.
 */
function applyPalette(palette: EmberColorPalette | null): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (!palette) {
    for (const cssVar of Object.keys(RGB_VARS)) {
      root.style.removeProperty(cssVar);
    }
    root.style.removeProperty('--color-hairline');
    return;
  }
  for (const [cssVar, key] of Object.entries(RGB_VARS)) {
    root.style.setProperty(cssVar, palette[key]);
  }
  root.style.setProperty('--color-hairline', palette.hairlineRgba);
}

/**
 * Eagerly hydrate :root with whatever palette was cached the last
 * time a Bond surface mounted. Called once at module load so the
 * page never paints in the brand default before async data arrives,
 * and so admin / inbox / memories surfaces (which don't fetch the
 * bond themselves) keep the user's Ember theme between visits.
 */
const cachedAtModuleLoad = readCachedPalette();
if (cachedAtModuleLoad) applyPalette(cachedAtModuleLoad);

interface UseEmberThemeOptions {
  /**
   * When true (default), a non-null palette is also written to
   * localStorage so subsequent loads can hydrate before the bond
   * fetch resolves.
   *
   * Set false for ephemeral palettes — the random sample applied to
   * anonymous visitors, or an admin viewing /admin/bonds/$id where
   * the page should temporarily wear the inspected bond's colors
   * without overwriting the operator's own bond palette in cache. In
   * that mode the cleanup ALSO restores the cached palette on unmount
   * so leaving the inspector reverts to the operator's own theme
   * rather than stranding the page on whichever bond they were
   * inspecting.
   */
  persistent?: boolean;
}

/**
 * Applies the Ember-derived theme to the document root. Persistent
 * mode (default) writes to localStorage so subsequent page loads
 * hydrate immediately. Ephemeral mode (`persistent: false`) skips the
 * cache write AND restores the cached palette on unmount, so a temp
 * theme switch (admin inspect, anonymous landing) doesn't leak past
 * the surface that requested it.
 */
export function useEmberTheme(
  palette: EmberColorPalette | null | undefined,
  options: UseEmberThemeOptions = {},
) {
  const { persistent = true } = options;
  useEffect(() => {
    if (palette === undefined) {
      // Caller hasn't finished loading — leave whatever is currently
      // applied (cached, or previous page's palette) in place rather
      // than flashing to brand default while we wait.
      return undefined;
    }
    applyPalette(palette);
    if (persistent) {
      writeCachedPalette(palette);
    }
    return () => {
      if (persistent) {
        // Persistent themes own the cache and the page — nothing to
        // restore on unmount; the next page either applies its own or
        // inherits the cached value via the module-load hydration.
        return;
      }
      // Ephemeral mode: snap back to whatever the cached palette is so
      // the operator's own bond colors return after the inspector
      // unmounts (or anonymous sample yields to a logged-in flow).
      applyPalette(readCachedPalette());
    };
  }, [palette, persistent]);
}

/**
 * Imperative cache invalidator. Use on logout so the next anonymous
 * load doesn't accidentally inherit the previous user's Ember theme.
 */
export function clearCachedEmberPalette(): void {
  writeCachedPalette(null);
  applyPalette(null);
}
